import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import type { StrengthClient } from "../types";

export function CopyTrainingDialog({
  target,
  clients,
  onClose,
  onCopied,
}: {
  target: StrengthClient;
  clients: StrengthClient[];
  onClose: () => void;
  onCopied: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [sourceId, setSourceId] = useState(target.id);
  const [sourceDate, setSourceDate] = useState(today);
  const [targetDate, setTargetDate] = useState(today);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const sourceClients = clients.filter(
    client => client.workspaceId === target.workspaceId
  );
  const sessions = trpc.silovyTrening.list.useQuery(
    { klientId: sourceId, datum: sourceDate, limit: 100 },
    { enabled: !!sourceDate }
  );
  const copy = trpc.silovyTrening.copy.useMutation();
  const submit = async () => {
    if (!sessionId || !targetDate) return;
    try {
      await copy.mutateAsync({
        sourceSessionId: sessionId,
        targetKlientId: target.id,
        targetDatum: targetDate,
      });
      toast.success(t("strengthProgress:copy.success"));
      onCopied();
      onClose();
    } catch {
      toast.error(t("strengthProgress:copy.error"));
    }
  };
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("strengthProgress:copy.title")}</DialogTitle>
          <DialogDescription>
            {t("strengthProgress:copy.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("strengthProgress:copy.sourceClient")}</Label>
            <Select
              value={String(sourceId)}
              onValueChange={v => {
                setSourceId(Number(v));
                setSessionId(null);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sourceClients.map(client => (
                  <SelectItem value={String(client.id)} key={client.id}>
                    {client.alias}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="copy-training-source-date">
              {t("strengthProgress:copy.sourceDate")}
            </Label>
            <Input
              id="copy-training-source-date"
              type="date"
              value={sourceDate}
              onChange={e => {
                setSourceDate(e.target.value);
                setSessionId(null);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("strengthProgress:copy.session")}</Label>
            <Select
              value={sessionId?.toString() ?? ""}
              onValueChange={v => setSessionId(Number(v))}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("strengthProgress:copy.chooseSession")}
                />
              </SelectTrigger>
              <SelectContent>
                {(sessions.data ?? []).map(session => (
                  <SelectItem key={session.id} value={String(session.id)}>
                    #{session.id} ·{" "}
                    {session.serie
                      .map(set => set.cvikNazov)
                      .filter(
                        (value, index, all) => all.indexOf(value) === index
                      )
                      .join(", ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sessions.isError ? (
              <p role="alert">{t("strengthProgress:messages.loadFailed")}</p>
            ) : !sessions.isLoading && sourceDate && !sessions.data?.length ? (
              <p className="text-sm text-muted-foreground">
                {t("strengthProgress:copy.empty")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="copy-training-target-date">
              {t("strengthProgress:copy.targetDate")}
            </Label>
            <Input
              id="copy-training-target-date"
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            disabled={!sessionId || !targetDate || copy.isPending}
            onClick={submit}
          >
            {t("strengthProgress:copy.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
